import { AddressFields, FormActions, getCurrenciesList } from '@common';
import { SelectField, TextareaField, TextField } from '@hypeit-gmbh/hypeit-ui';
import { Button, Fieldset, Grid, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

const TRANS_KEY = 'ADD_BANK_ACCOUNT_FORM';
const VIEW_TRANS_KEY = 'VIEW_DELETED_BANK_ACCOUNT';

type BankAccountFormFieldsPropsType = {
  onClose: () => void;
  readOnly?: boolean;
  deletionReason?: string | null;
};

export const BankAccountFormFields: React.FC<
  BankAccountFormFieldsPropsType
> = ({ onClose, readOnly = false, deletionReason = null }) => {
  const { t } = useTranslation('common');

  return (
    <Stack>
      {readOnly && deletionReason && (
        <Stack>
          <Text fw={600} size="sm">
            {t(`${VIEW_TRANS_KEY}.DELETION_REASON_LABEL`)}
          </Text>
          <Text size="sm" c="dimmed">
            {deletionReason}
          </Text>
        </Stack>
      )}

      <Stack>
        <TextField
          fieldName="accountHolderName"
          label={t(`${TRANS_KEY}.ACCOUNT_HOLDER_NAME`)}
          readOnly={readOnly}
        />
        <AddressFields.Root
          fieldPrefix="accountHolderAddress"
          legend={t(`${TRANS_KEY}.ACCOUNT_HOLDER_ADDRESS_LABEL`)}
        >
          <AddressFields.Street required={false} readOnly={readOnly} />
          <AddressFields.Addition readOnly={readOnly} />
          <AddressFields.Zip required={false} readOnly={readOnly} />
          <AddressFields.City required={false} readOnly={readOnly} />
          <AddressFields.Country required={false} readOnly={readOnly} />
        </AddressFields.Root>
      </Stack>

      <Stack>
        <TextField
          fieldName="bankName"
          label={t(`${TRANS_KEY}.BANK_NAME`)}
          readOnly={readOnly}
        />
        <AddressFields.Root
          fieldPrefix="bankAddress"
          legend={t(`${TRANS_KEY}.BANK_ADDRESS_LABEL`)}
        >
          <AddressFields.Street required={false} readOnly={readOnly} />
          <AddressFields.Addition readOnly={readOnly} />
          <AddressFields.Zip required={false} readOnly={readOnly} />
          <AddressFields.City required={false} readOnly={readOnly} />
          <AddressFields.Country required={false} readOnly={readOnly} />
        </AddressFields.Root>
      </Stack>

      <Fieldset legend={t(`${TRANS_KEY}.ACCOUNT_SECTION`)}>
        <Grid>
          <Grid.Col span={6}>
            <TextField
              fieldName="accountNumber"
              label={t(`${TRANS_KEY}.ACCOUNT_NUMBER`)}
              readOnly={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextField
              fieldName="bicSwift"
              label={t(`${TRANS_KEY}.BIC_SWIFT`)}
              readOnly={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextField
              fieldName="iban"
              label={t(`${TRANS_KEY}.IBAN`)}
              readOnly={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextField
              fieldName="routingNumber"
              label={t(`${TRANS_KEY}.ROUTING_NUMBER`)}
              readOnly={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <SelectField
              fieldName="currency"
              label={t(`${TRANS_KEY}.CURRENCY`)}
              data={getCurrenciesList()}
              allowDeselect={false}
              disabled={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextareaField
              fieldName="notes"
              label={t(`${TRANS_KEY}.NOTES`)}
              readOnly={readOnly}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>

      <Fieldset legend={t(`${TRANS_KEY}.EVENTIM_SECTION`)}>
        <TextField
          fieldName="creditorNumber"
          label={t(`${TRANS_KEY}.CREDITOR_NUMBER`)}
          readOnly={readOnly}
        />
      </Fieldset>

      {readOnly ? (
        <Group justify="end">
          <Button variant="outline" onClick={onClose} color="blue">
            {t(`${VIEW_TRANS_KEY}.CLOSE_BUTTON`)}
          </Button>
        </Group>
      ) : (
        <FormActions
          onCancel={onClose}
          saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
          cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
        />
      )}
    </Stack>
  );
};
